import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import Spinner from '@/components/UI/Spinner/Spinner';
import { addCollapsedDataWorkflow, sameArraysNoMatterOrder } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useError from '@/hooks/useError';
import { setCampaignVehicles, setOriginalValues, setLimitationPlants } from '@/redux/Campaign/campaign.action';
import {
  getCampaignVehicles, getCampaignHelp, getOriginalValues, getCampaignInfo, getAffectedVehiclesValidations,
  getCampaignVehiclesTechAssessment, getCampaignValidationsActive, getArePendingVolumes, getSelectedPlants, getVolumeHeaderPlants,
} from '@/redux/Campaign/campaign.reducer';
import { uniqueSeriesPlants } from '@/redux/Campaign/campaign.utils';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { setCampaignWorkflow } from '@/redux/Workflow/workflow.action';
import campaignServices from '@/services/campaignServices';
import { AffectedVehicles as AffectedVehiclesRedux, AffectedVehiclesBackGet, LimitationVolumesBackGet, CampaignInfo,
  LimitationPlant, AffectedVehiclesOriginalValues, LimitationSerie, Validations, Workflow,
  ObjLimitationPlant,
} from '@/types';
import SeriesPanel from './SeriesPanel/SeriesPanel';
import { seriesToArray, isViaPlants, mapAffectedVehicle } from './SeriesPanel/SeriesRow/series-row-utils';
import TechAssessment from './TechAssessment/TechAssessment';
import Volume from './Volume/Volume';
import { filterGroupsByAllowedMS } from './Volume/VolumeDetails/VehicleCleanDirt/VehicleCleanDirt-utils';
import { mapAggregateModelSelections } from './Volume/VolumeDetails/volumeDetails-utils';

interface AffectedVehiclesProps {
  affectedVehicles: AffectedVehiclesRedux;
  campaignInfo: CampaignInfo;
  affectedVehiclesValidations: Validations | any;
  headerLanguage: string;
  timeZone: string;
  validationsActive: boolean;
  pendingVolumes: boolean;
  originalValues: AffectedVehiclesOriginalValues;
  plantsSelected: LimitationPlant[];
  headerPlants: LimitationSerie[];
  setAffectedVehicles: (affectedVehicles: AffectedVehiclesRedux) => void;
  setOriginValues: (originalValues: AffectedVehiclesOriginalValues) => void;
  setLimitPlants: (limitationPlants: { [key: string]: ObjLimitationPlant }) => void;
  setWorkflow: (workflow: Workflow) => void;
}

const AffectedVehicles: React.FC<AffectedVehiclesProps> = (props) => {
  const { affectedVehicles, setAffectedVehicles, setOriginValues, campaignInfo, affectedVehiclesValidations,
    headerLanguage, timeZone, validationsActive, pendingVolumes, setLimitPlants, originalValues, plantsSelected,
    setWorkflow, headerPlants } = props;

  const { id, version: urlVersion } = useParams<{ id: string; version: string }>();
  const version = urlVersion || campaignInfo?.version;
  const isLoading = !affectedVehicles?.id;
  const checkDisabled = useCheckDisabled(true);
  const { handleCriticalError } = useError();
  const { t } = useTranslation();
  const checkReadOnly = campaignInfo.authorization?.readOnly;

  const onCopyOriginValues = (content, hasMSChange) => {
    const copyValues = {
      ...originalValues,
      ...content,
      hasChange: hasMSChange,
    };
    setOriginValues(copyValues);
  };

  const volumePanelIsInvalid = () => {
    const someVolumeInvalid = Object.values(affectedVehiclesValidations?.[headerLanguage]?.volumes || {})
      .some((value: any) => value.invalid);
    const noVolumes = Object.values(affectedVehiclesValidations?.[headerLanguage]?.volumes || {}).length === 0;

    return (someVolumeInvalid || noVolumes) && validationsActive;
  };

  const fetchAffectedVehicles = useApiRequest(campaignServices.getCampaignRetryWithSignal);

  useEffect(() => {
    if (!pendingVolumes) {
      Promise.all([
        fetchAffectedVehicles(id, 'affected-vehicles', version),
        campaignServices.getCampaignRetry(id, 'limitation-volumes', version),
      ]).then(async (res: [AffectedVehiclesBackGet, LimitationVolumesBackGet]) => {
        let hasMSChange = false;
        const [affVehicles, limitationVolumes] = res;
        const series = seriesToArray(affVehicles?.affectedSeries);
        const plants = uniqueSeriesPlants(series);
        const affVehiclesMapped = mapAffectedVehicle(affVehicles);
        const volumes = mapAggregateModelSelections(limitationVolumes.volumes, timeZone);
        let newState = { ...affVehiclesMapped, series, volumes };

        if (!isViaPlants(affVehiclesMapped) && plants?.length > 0) {
          const limitationPlants: { [key: string]: ObjLimitationPlant } = {};
          affVehicles?.affectedPlants?.forEach((plant) => {
            limitationPlants[plant.wkzNumber] = plant as unknown as ObjLimitationPlant;
          });
          const [newAffectedVehiclesVolumes, hasChanges] = filterGroupsByAllowedMS(volumes, limitationPlants);
          hasMSChange = hasChanges;
          newState = { ...newState, volumes: newAffectedVehiclesVolumes };
          setLimitPlants(limitationPlants);
        }

        onCopyOriginValues(newState, hasMSChange);
        setAffectedVehicles(newState);

        // Update workflow if plants change ( workflow changes depending of workflowtype of plants )
        const currentPlants = (isViaPlants(affVehicles) ? plantsSelected : headerPlants)?.map((p) => p.wkzNumber);
        const lastPlants = originalValues?.affectedPlants?.map((p) => p.wkzNumber) || [];

        if (!sameArraysNoMatterOrder(lastPlants, currentPlants)) {
          const workflow = await campaignServices.getCampaignRetry(id, 'workflows', version);
          setWorkflow({
            ...workflow,
            statuses: addCollapsedDataWorkflow(workflow.statuses, t, timeZone),
            historicalStatuses: addCollapsedDataWorkflow(workflow.historicalStatuses || [], t, timeZone),
          });
        }
      }).catch(handleCriticalError);
    }
  }, [pendingVolumes]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <TechAssessment />
          <SeriesPanel
            checkEnabled={checkDisabled}
            invalid={(!!affectedVehiclesValidations?.[headerLanguage]?.seriesInvalid) && validationsActive}
          />
          <Volume
            isReadOnly={checkReadOnly}
            invalid={volumePanelIsInvalid()}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  affectedVehicles: getCampaignVehicles(state),
  campaignHelp: getCampaignHelp(state),
  originalValues: getOriginalValues(state),
  campaignInfo: getCampaignInfo(state),
  affectedVehiclesValidations: getAffectedVehiclesValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  techAssessment: getCampaignVehiclesTechAssessment(state),
  timeZone: getSettingsInfo(state)?.timezone,
  validationsActive: getCampaignValidationsActive(state)?.all,
  pendingVolumes: getArePendingVolumes(state),
  plantsSelected: getSelectedPlants(state) || [],
  headerPlants: getVolumeHeaderPlants(state) as unknown as LimitationSerie[] || [],
});

const actionCreators = {
  setAffectedVehicles: setCampaignVehicles,
  setOriginValues: setOriginalValues,
  setHelpArea: setAreaHelp,
  setLimitPlants: setLimitationPlants,
  setWorkflow: setCampaignWorkflow,
};

export default connect(mapStateToProps, actionCreators)(AffectedVehicles);
